/**
 * The "what does this page actually see?" snapshot behind the bug icon in the app
 * bar. The UI is a pure projection of a handful of live documents plus a few
 * derivations over them (host presentation from the heartbeat, the phase from the
 * release facts, which instruction is in flight), so when it shows something
 * confusing the fastest way to reason about it is to look at exactly those inputs
 * and derivations together — not screenshots, and not a Firestore console that
 * shows the documents without the derivations. This module assembles both into
 * one JSON blob an operator can copy into a chat or a bug report.
 *
 * Pure and React-free so the assembly and the serialization are unit-tested; the
 * dialog only feeds it the subscribed documents.
 */
import {
    hostInstance,
    legalInstructions,
    phaseFrom,
    type HostDoc,
    type HostRole,
    type InstructionKind,
    type InstructionStatus,
    type Phase,
    type ReleaseFacts,
    type UnreleasedDoc,
} from '@finvari/deployer';
import type { Loadable } from '../hooks';
import { unreleasedView, type UnreleasedView } from './unreleased';
import { newCommitsOnMain, nextPrepareStep, type NewMainCommits, type NextPrepareAction } from './prepareGuidance';
import { hostView, type HostView } from './hostStatus';
import { systemView, type SystemView } from './releaseSystem';
import { inflightRows, latestByKind, type InstructionRow } from './instructionActivity';
import {
    activeDeployRelease,
    activePrepareRelease,
    homeFacts,
    homeSourcesLoading,
    showsDeployingView,
    showsLoadingView,
    showsPreparingView,
    type DeployRelease,
    type HomeInput,
    type PrepareRelease,
} from './homeMode';

/**
 * How much of each instruction's `tail` (the host's last ~4 KB of output) to
 * keep. Twenty-five instructions per host at 4 KB each would make the snapshot
 * mostly log text and too large to paste; the structured fields (`status`,
 * `error`, `rejectedReason`, `result`) carry the state, and the full log is one
 * click away on the instruction page.
 */
export const TAIL_KEEP_CHARS = 240;

/** Everything the dialog hands over: the raw subscribed documents plus context. */
export interface DebugSnapshotInput {
    /** The clock the derivations use — the same `now` the page renders with. */
    now: number;
    /** `location.pathname` — which page the operator was looking at. */
    route: string;
    /** Build-time and SDK facts: project id, emulator flag, SDK version, mode. */
    env: Record<string, string | boolean | undefined>;
    operator: { uid: string; email: string };
    /**
     * Each host document beside whether its first snapshot has arrived.
     *
     * The flag travels with the document because the derivations turn on it and the
     * absent value cannot be told apart from the answer: an unread host reads as
     * `unknown`, which makes the system `asleep`, which would have the snapshot
     * report that the sleeping panel was on screen while the page was showing its
     * spinner. The same reason the pages carry it (`Loadable` in `hooks`).
     */
    hosts: Record<HostRole, Loadable<HostDoc | undefined>>;
    prepareReleases: Loadable<PrepareRelease[]>;
    /** The `(default)` release documents — the deploy half's projection. */
    deployReleases: Loadable<DeployRelease[]>;
    /**
     * `main/unreleased` — the last "is there anything to release?" check. The value
     * alone, unlike the hosts above, because the page reads it the same way: an
     * unread check shows as "never checked" on screen too.
     */
    unreleased: UnreleasedDoc | undefined;
    /** The recent-instruction window per host, newest first (as the hooks return it). */
    instructions: Record<HostRole, InstructionRow[]>;
}

/** One instruction as the snapshot lists it: the document, with its tail trimmed. */
export type SnapshotInstruction = Omit<InstructionRow, 'tail'> & { tail?: string };

export interface HostSnapshot {
    /** The presentation the card derives — offline/online/… — and its inputs. */
    view: HostView;
    doc: HostDoc | undefined;
}

export interface InstructionsSnapshot {
    /** Pending/running instruction ids, oldest first (the order the host runs them). */
    inflight: string[];
    /** The newest instruction of each kind — what every action card keys its status off. */
    latestByKind: Partial<Record<InstructionKind, { id: string; status: InstructionStatus }>>;
    recent: SnapshotInstruction[];
}

export interface DebugSnapshot {
    capturedAt: string;
    route: string;
    env: DebugSnapshotInput['env'];
    operator: DebugSnapshotInput['operator'];
    /** How the home page chose its mode: the phase, the facts behind it, and the answer. */
    home: {
        /**
         * The page was still reading and showed none of what follows. First, because
         * it overrules the rest: every field below is derived from documents that had
         * not arrived, so a snapshot taken here describes a page that was a spinner.
         */
        loading: boolean;
        phase: Phase;
        showsPreparingView: boolean;
        showsDeployingView: boolean;
        activeReleaseId: string | undefined;
        /** The active `(default)` release, which need not be the same one. */
        activeDeployReleaseId: string | undefined;
        facts: ReleaseFacts;
        /** Why the idle page does or does not offer to start a release. */
        unreleased: UnreleasedView;
        /**
         * The action the preparing page puts in front of the operator, and whether
         * it sees new commits on main. The whole complaint behind this change was
         * "no buttons are enabled anywhere" — a snapshot that reports the phase and
         * the legality table but not what the page *concluded* from them cannot
         * distinguish a page with nothing to offer from one that offered the wrong
         * thing.
         */
        nextStep: NextPrepareAction;
        newCommitsOnMain: NewMainCommits;
        /**
         * Whether the page showed the flow or the sleeping panel, and the inputs that
         * decided it. The panel replaces everything, so a snapshot taken from it
         * otherwise reports a phase and a next step that were never on screen —
         * "asleep, needs the release host, would wake both" is the whole explanation.
         */
        system: SystemView;
    };
    hosts: Record<HostRole, HostSnapshot>;
    /** The check the idle page reads, beside the view derived from it. */
    unreleasedDoc: UnreleasedDoc | undefined;
    prepareReleases: PrepareRelease[];
    deployReleases: DeployRelease[];
    instructions: Record<HostRole, InstructionsSnapshot>;
    /** Which actions the shared legality table allows in this phase, per host. */
    legal: Record<HostRole, ReturnType<typeof legalInstructions>>;
}

/** Keep only the end of a tail — the end is where the outcome is. */
export function trimTail(tail: string | undefined): string | undefined {
    if (tail === undefined || tail.length <= TAIL_KEEP_CHARS) return tail;
    const dropped = tail.length - TAIL_KEEP_CHARS;
    return `…[${dropped} chars omitted] ` + tail.slice(-TAIL_KEEP_CHARS);
}

function summarizeInstructions(rows: InstructionRow[]): InstructionsSnapshot {
    const latest: InstructionsSnapshot['latestByKind'] = {};
    for (const [kind, row] of latestByKind(rows)) latest[kind] = { id: row.id, status: row.status };
    return {
        inflight: inflightRows(rows).map((r) => r.id),
        latestByKind: latest,
        recent: rows.map((r) => ({ ...r, tail: trimTail(r.tail) })),
    };
}

/** Assemble the snapshot: the raw inputs beside every derivation the pages make from them. */
export function buildDebugSnapshot(input: DebugSnapshotInput): DebugSnapshot {
    const active = activePrepareRelease(input.prepareReleases.value);
    const deploy = activeDeployRelease(input.deployReleases.value);
    const home: HomeInput = {
        prepare: active,
        deployReleases: input.deployReleases.value,
        prepareHost: input.hosts.prepare.value,
        releaseHost: input.hosts.release.value,
        prepareInstructions: input.instructions.prepare,
        releaseInstructions: input.instructions.release,
    };
    const facts = homeFacts(home);
    const phase = phaseFrom(facts);
    // Through the same `loading` argument the pages pass, so a host whose snapshot
    // has not arrived is `loading` here as well and never `unknown`.
    const prepareView = hostView(input.hosts.prepare.value, input.now, input.hosts.prepare.loading);
    const releaseView = hostView(input.hosts.release.value, input.now, input.hosts.release.loading);
    const system = systemView(phase, {
        prepare: { view: prepareView, expectedBootSeconds: hostInstance('prepare')?.expectedBootSeconds },
        release: { view: releaseView, expectedBootSeconds: hostInstance('release')?.expectedBootSeconds },
    });
    const sourcesLoading = homeSourcesLoading({
        prepareHost: input.hosts.prepare.loading,
        releaseHost: input.hosts.release.loading,
        prepareReleases: input.prepareReleases.loading,
        deployReleases: input.deployReleases.loading,
    });
    return {
        capturedAt: new Date(input.now).toISOString(),
        route: input.route,
        env: input.env,
        operator: input.operator,
        home: {
            loading: showsLoadingView(sourcesLoading, system),
            phase,
            showsPreparingView: showsPreparingView(active, phase),
            showsDeployingView: showsDeployingView(phase),
            activeReleaseId: active?.id,
            activeDeployReleaseId: deploy?.id,
            facts,
            unreleased: unreleasedView(input.unreleased, input.hosts.prepare.value, input.now),
            nextStep: nextPrepareStep(active?.steps, phase),
            newCommitsOnMain: newCommitsOnMain(input.unreleased, input.hosts.prepare.value),
            system,
        },
        hosts: {
            prepare: { view: prepareView, doc: input.hosts.prepare.value },
            release: { view: releaseView, doc: input.hosts.release.value },
        },
        unreleasedDoc: input.unreleased,
        prepareReleases: input.prepareReleases.value,
        deployReleases: input.deployReleases.value,
        instructions: {
            prepare: summarizeInstructions(input.instructions.prepare),
            release: summarizeInstructions(input.instructions.release),
        },
        legal: {
            prepare: legalInstructions(phase, 'prepare'),
            release: legalInstructions(phase, 'release'),
        },
    };
}

/** Duck-typed Firestore `Timestamp` (both SDKs' classes satisfy the shared shape). */
function isTimestampLike(value: unknown): value is { toMillis(): number } {
    return (
        typeof value === 'object' &&
        value !== null &&
        typeof (value as { toMillis?: unknown }).toMillis === 'function' &&
        typeof (value as { seconds?: unknown }).seconds === 'number'
    );
}

/**
 * Convert to plain JSON-ready data. Firestore `Timestamp`s become ISO strings —
 * the SDK's own `toJSON` gives `{seconds, nanoseconds}`, which nobody can read
 * against a "finished 11m ago" caption without a calculator. Walked explicitly
 * rather than via a `JSON.stringify` replacer because the replacer runs *after*
 * `toJSON`, by which point the Timestamp is already the unreadable pair.
 */
export function toPlain(value: unknown): unknown {
    if (isTimestampLike(value)) return new Date(value.toMillis()).toISOString();
    if (Array.isArray(value)) return value.map(toPlain);
    if (typeof value === 'object' && value !== null) {
        const out: Record<string, unknown> = {};
        for (const [k, v] of Object.entries(value)) {
            if (typeof v === 'function') continue;
            out[k] = toPlain(v);
        }
        return out;
    }
    return value;
}

/** The snapshot as the text the Copy button puts on the clipboard. */
export function serializeSnapshot(snapshot: DebugSnapshot): string {
    return JSON.stringify(toPlain(snapshot), null, 2);
}
